import { Heart } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Reveal, Eyebrow } from "../components/Reveal";
import { scrollToId } from "../lib/smooth";

const ease = [0.16, 1, 0.3, 1];
const photos = [
  ["/images/p-controller-robot.jpg", "Robot hand controller stand", "-rotate-3", "var(--yellow)"],
  ["/images/p-name-signs.jpg", "Personalised letter name signs", "rotate-2", "var(--cyan)"],
  ["/images/p-crystal-dragon.jpg", "Articulated crystal dragon", "-rotate-1", "var(--pink)"],
];

const Collage = () => (
  <div className="grid grid-cols-3 gap-3 sm:gap-5" data-testid="about-collage">
    {photos.map(([src, alt, rot, bg], i) => (
      <motion.div key={src} initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.4 + i * 0.12, ease }} className={`${rot} ${i === 1 ? "translate-y-6" : ""}`}>
        <div className="rounded-[22px] chunk p-2 sm:p-3" style={{ background: bg }}>
          <div className="aspect-[3/4] rounded-[14px] overflow-hidden border-2 border-[var(--ink)]">
            <img src={src} alt={alt} className="w-full h-full object-cover" />
          </div>
        </div>
      </motion.div>
    ))}
  </div>
);

export default function About() {
  return (
    <main className="pt-28 sm:pt-36 pb-24 layer-lines overflow-hidden" data-testid="about-page">
      <section className="px-4 sm:px-8 max-w-6xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-14 items-center">
        <div className="lg:col-span-6">
          <Eyebrow color="var(--pink)"><Heart className="w-3.5 h-3.5 fill-current" /> About us</Eyebrow>
          <h1 className="font-display font-extrabold tracking-tighter leading-[0.92] text-5xl sm:text-6xl lg:text-7xl mt-6">
            {["About", "Scratch's", "3D Prints"].map((t, i) => (
              <span key={t} className="block overflow-hidden pb-[0.06em]">
                <motion.span className={`block ${i === 1 ? "font-serif-i font-normal text-[var(--pink)]" : ""}`}
                  initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1, delay: 0.1 + i * 0.12, ease }}>
                  {t}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.55, ease }}
            className="mt-8 text-lg sm:text-xl leading-relaxed max-w-xl" data-testid="about-intro">
            Scratch's 3D Prints is more than just a 3D printing project. It's a way to honour Blair's late grandfather, affectionately known as Scratch, and keep his memory alive while building something special of our own.
          </motion.p>
        </div>
        <div className="lg:col-span-6"><Collage /></div>
      </section>

      <section className="px-4 sm:px-8 max-w-6xl mx-auto mt-24 sm:mt-32 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Reveal className="h-full">
          <div className="h-full rounded-[28px] chunk bg-white p-7 sm:p-10" data-testid="about-family">
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight">A small family-run project</h2>
            <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#3b3550]">
              We love turning creative ideas into practical, personalised and unique creations. From key rings and bookmarks to name plaques, ring holders and controller stands, every print is an opportunity to make something someone can enjoy, use or gift to someone special.
            </p>
          </div>
        </Reveal>
        <Reveal className="h-full" delay={0.1}>
          <div className="h-full rounded-[28px] chunk bg-[var(--cyan)] p-7 sm:p-10" data-testid="about-journey">
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight">Growing one print at a time</h2>
            <p className="mt-4 text-base sm:text-lg leading-relaxed">
              We're creating this profile to share our journey, showcase our creations, connect with our community, and bring your ideas to life.
            </p>
            <p className="mt-4 text-base sm:text-lg leading-relaxed">
              We're starting small, learning as we go, and growing one print at a time. Thank you for supporting our little project and helping us carry Scratch's name forward.
            </p>
          </div>
        </Reveal>
      </section>

      <Reveal className="px-4 sm:px-8 max-w-6xl mx-auto mt-16 sm:mt-24">
        <div className="rounded-[32px] chunk bg-[var(--ink)] text-[var(--paper)] p-8 sm:p-14 relative overflow-hidden grain" data-testid="about-signoff">
          <Heart className="w-10 h-10 text-[var(--pink)] fill-current" />
          <p className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mt-6 max-w-3xl">
            Made with love, inspired by family, and printed <span className="font-serif-i font-normal text-[var(--yellow)]">one creation at a time.</span>
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/" onClick={() => setTimeout(() => scrollToId("shop"), 350)} data-testid="about-shop-btn"
              className="press chunk-sm rounded-full bg-[var(--pink)] text-white px-6 py-3 font-bold">Browse the shop</Link>
            <Link to="/" onClick={() => setTimeout(() => scrollToId("custom"), 350)} data-testid="about-custom-btn"
              className="press chunk-sm rounded-full bg-[var(--yellow)] text-[var(--ink)] px-6 py-3 font-bold">Ask about a custom print</Link>
          </div>
        </div>
      </Reveal>
    </main>
  );
}
